import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Card } from '@/components';
import { validateEmail } from '@/utils/validation';
import { supabase } from '@/lib/supabase';

export const ForgotPasswordPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Reset Your Password | PrintDeck Direct';
  }, []);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [canResendLink, setCanResendLink] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(0);

  const handleRequestReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailError('');
    setSubmitError('');

    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    setIsLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) throw resetError;

      setIsLinkSent(true);
      setCanResendLink(false);
      setResendCountdown(60);

      // Countdown timer for resending
      const interval = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResendLink(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to request password reset. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendLink = async () => {
    setSubmitError('');
    setIsLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) throw resetError;

      setCanResendLink(false);
      setResendCountdown(60);

      const interval = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResendLink(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to resend reset link.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">
              Reset Password
            </h1>
            <p className="text-gray-600 mt-2 text-sm">
              {isLinkSent 
                ? 'Check your inbox for a verification email' 
                : 'Enter your email to receive a reset link'}
            </p>
          </div>

          {/* Error Banner */}
          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
              <p className="text-red-800 text-xs">✕ {submitError}</p>
            </div>
          )}

          {/* Step 1: Email Form */}
          {!isLinkSent && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <Input
                id="email"
                label="Email Address"
                type="email"
                name="email"
                placeholder="you@printshop.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={emailError}
                fullWidth
                disabled={isLoading}
              />

              <Button
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                isLoading={isLoading}
                disabled={isLoading}
              >
                Send Reset Link
              </Button>
            </form>
          )}

          {/* Step 2: Link Sent Success Screen */}
          {isLinkSent && (
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto border border-emerald-100">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 19v-8.93a2 2 0 01.89-1.664l8-5.333a2 2 0 012.22 0l8 5.333A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-2.25-1.5a2 2 0 00-2.22 0l-2.25 1.5" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Check your email</h2>
              <p className="text-xs text-gray-600 leading-relaxed">
                We've sent a secure password reset link to <strong>{email}</strong>. Please check your inbox and click the link to set a new password.
              </p>
              <div className="pt-2">
                {canResendLink ? (
                  <button
                    type="button"
                    onClick={handleResendLink}
                    disabled={isLoading}
                    className="text-blue-600 hover:text-blue-700 font-semibold text-xs disabled:text-gray-400"
                  >
                    Resend reset link
                  </button>
                ) : (
                  <p className="text-gray-500 text-xs">
                    Resend link in {resendCountdown}s
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-2">
            <p className="text-gray-600 text-xs">
              Remember your password?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
